// Категория: модуль с побочным эффектом ровно одного вида — динамическим import().
//
// Подпись тела запроса — единственный анти-abuse механизм перед /api/convert, и её
// чанк грузится отдельно. Вся обвязка вокруг этого жила методами корневого Vue-инстанса
// в app.js, хотя к жизненному циклу инстанса отношения не имеет: это обычный клиент
// с состоянием, и здесь его можно проверить отдельно от монтирования приложения.
//
// Подробности механизма — в README, раздел «Подпись тела запроса».

// Код отказа берём из контрактов, а не дублируем литералом: его же читает
// buildConvertErrorResult, и разошедшиеся значения молча сломали бы распознавание
// fail-closed. downloadContracts.js сам ни от чего не зависит, так что цикла нет.
import { SIGNING_UNAVAILABLE_CODE } from './downloadContracts';

// Сколько ждать уже запущенную загрузку в момент самого запроса.
const SIGNED_REQUEST_WAIT_TIMEOUT = 10000;

// Потолок для самого import(): запрос за чанком может не завершиться вообще, если его
// глушит расширение или прокси, и тогда промис висел бы pending навсегда.
const SIGNED_REQUEST_CHUNK_TIMEOUT = 30000;

// Сколько ПОПЫТОК загрузки подряд могут закончиться неподписанным телом, прежде чем
// перейти в fail-closed. Считаем попытки, а не вызовы: всплеск параллельных карточек
// ждёт один и тот же промис, и три таких ожидания — одна неудача, а не три.
const MAX_UNSIGNED_FALLBACKS = 3;

// Итог гонки «подпись против таймаута». Таймаут и подтверждённый сбой загрузки дают
// один и тот же фолбэк, но это разные ситуации, и в консоли их надо различать.
const SIGNING_PENDING = Symbol('signing-pending');

/**
 * Тело запроса без подписи — то, что уходит, пока настоящий модуль не доехал.
 * @param requestData {string|Object}
 * @returns {Promise<Object>}
 */
export const buildUnsignedRequestBody = async (requestData) => (
    typeof requestData === 'string' ? { url: requestData } : requestData
);

/**
 * Клиент подписи запроса.
 *
 * @param options {Object}
 * @param options.load {Function} - загрузчик модуля подписи, возвращает промис с default
 * @param options.waitTimeout {number}
 * @param options.chunkTimeout {number}
 * @param options.maxUnsignedFallbacks {number}
 * @returns {{start: Function, signBody: Function}}
 */
export function createSignedRequestClient({
    load,
    waitTimeout = SIGNED_REQUEST_WAIT_TIMEOUT,
    chunkTimeout = SIGNED_REQUEST_CHUNK_TIMEOUT,
    maxUnsignedFallbacks = MAX_UNSIGNED_FALLBACKS,
} = {}) {
    // настоящая функция подписи, когда чанк доехал
    let sign = null;
    // промис текущей попытки загрузки; null — попытки не было или прошлая провалилась
    let loading = null;
    // сколько попыток закончилось неподписанным телом
    let unsignedFallbacks = 0;
    // за какую попытку фолбэк уже посчитан — чтобы параллельные ожидания одной и той
    // же загрузки не тратили бюджет по отдельности
    let countedFor = null;

    function start() {
        let chunkTimeoutId;
        const chunkDeadline = new Promise((_, reject) => {
            chunkTimeoutId = setTimeout(
                () => reject(new Error('signing chunk load timed out')),
                chunkTimeout,
            );
        });

        const loaded = load();

        // Подписка на сам load(), а не только на исход гонки. Если чанк доедет позже
        // chunkTimeout, гонку выиграет дедлайн, и подписчиков на поздний успех не
        // останется вовсе — подпись была бы потеряна, хотя модуль доступен.
        loaded.then((module) => {
            if (module && module.default) {
                sign = module.default;
                unsignedFallbacks = 0;
                countedFor = null;
            }
        }, () => {
            // отказ разбирает цепочка ниже, здесь молчим, чтобы не плодить
            // необработанный reject
        });

        const attempt = Promise.race([loaded, chunkDeadline])
            .then((module) => module.default)
            .catch((error) => {
                console.warn('[signed-request] failed to load the signing module', error);
                // сбрасываем, чтобы следующий запрос попробовал заново: иначе одна
                // сетевая ошибка снимала бы подпись до конца жизни страницы
                if (loading === attempt) {
                    loading = null;
                }
                return null;
            })
            .finally(() => {
                clearTimeout(chunkTimeoutId);
            });

        loading = attempt;
        return attempt;
    }

    function fallback(attempt) {
        if (countedFor !== attempt) {
            countedFor = attempt;
            unsignedFallbacks += 1;
        }

        if (unsignedFallbacks > maxUnsignedFallbacks) {
            console.error('[signed-request] signing is still unavailable, refusing to send unsigned requests');
            const error = new Error('Request signing is unavailable');
            error.code = SIGNING_UNAVAILABLE_CODE;
            throw error;
        }

        return buildUnsignedRequestBody;
    }

    /**
     * Возвращает функцию подписи, а при её недоступности — неподписанный фолбэк.
     * Отсчёт ожидания идёт отсюда, а не от загрузки страницы: иначе окно без подписи
     * открывалось бы на первые секунды каждой сессии независимо от того, нужен ли был
     * запрос в этот момент.
     * @returns {Promise<Function>}
     */
    async function resolve() {
        if (sign) {
            unsignedFallbacks = 0;
            countedFor = null;
            return sign;
        }

        if (!loading) {
            start();
        }
        const attempt = loading;

        let waitTimeoutId;
        const deadline = new Promise((resolveDeadline) => {
            waitTimeoutId = setTimeout(() => resolveDeadline(SIGNING_PENDING), waitTimeout);
        });

        try {
            const resolved = await Promise.race([attempt, deadline]);

            if (resolved === SIGNING_PENDING) {
                console.warn('[signed-request] the signing module is still loading, sending an unsigned body');
                return fallback(attempt);
            }
            if (!resolved) {
                console.warn('[signed-request] the signing module is unavailable, sending an unsigned body');
                return fallback(attempt);
            }

            return resolved;
        } finally {
            clearTimeout(waitTimeoutId);
        }
    }

    return {
        // запускает загрузку заранее, ничего не дожидаясь
        start,
        /**
         * Готовое тело запроса. Сбой САМОЙ подписи наружу не глушим: неподписанное
         * тело здесь превратило бы детерминированную ошибку сигнера в гарантированный
         * обход анти-abuse. Фолбэк живёт только в resolve(), где означает «модуль
         * недоступен», а не «подпись упала».
         * @param url {string}
         * @returns {Promise<Object>}
         */
        async signBody(url) {
            const signer = await resolve();
            return signer(url);
        },
    };
}
